import { Button } from '@/components/atoms/Button';
import { XIcon } from '@/components/atoms/icons';
import { Logo } from '@/components/atoms/Logo';
import { TextInput } from '@/components/atoms/TextInput';
import { Typography } from '@/components/atoms/Typography';
import { FullScreenLoader } from '@/components/molecules';
import { OTPInput } from '@/components/molecules/OTPInput';
import { Toast } from '@/components/molecules/Toast';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useEmiratesIdOtpResendSeconds, useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
    trackEmiratesIdLoginStartClick,
    trackEmiratesIdRaiseSupportClick,
} from '@/core/services/trackDynatraceCatalog';
import { insertOtpResendCountdownInTemplate } from '@/core/utils/otpResendTimerDisplay';
import { useEmiratesIdLogin } from '@/modules/auth/hooks/useEmiratesIdLogin';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Modal, Pressable, StyleSheet, View } from 'react-native';

/**
 * Mobile Emirates ID login — same sign-in → SMS OTP → email OTP → JWT flow
 * as web LoginDialog. Web dialog is unchanged.
 */

const EMIRATES_ID_PREFIX = '784';
const EMIRATES_ID_PREFIX_FORMATTED = '784 - ';
// Scoped to this sheet only — the shared fontErrorText token stays at its current
// (marketing-approved) color; this instance override is specific to this screen's request.
const ERROR_TEXT_COLOR = '#D8181E';

export interface EmiratesIdLoginSheetProps {
    visible: boolean;
    onClose: () => void;
}

/**
 * Unicode LEFT-TO-RIGHT EMBEDDING … POP DIRECTIONAL FORMATTING. Keeps a phone
 * number / email reading exactly as in English inside Arabic copy — without it
 * the bidi algorithm moves the leading '+' to the end and reorders digit groups.
 * Marks (not a style) because the number is an inline nested Typography span.
 */
function forceLtr(text: string): string {
    return `‪${text}‬`;
}

/** Email display: ja***************.com (first 2 + asterisks + TLD). */
function maskEmailForOtp(email: string): string {
    const trimmed = email.trim();
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 2) {
        return trimmed;
    }
    const prefix = trimmed.slice(0, 2);
    const tld = trimmed.slice(lastDot);
    return `${prefix}${'*'.repeat(trimmed.length - prefix.length - tld.length)}${tld}`;
}

/** Index (within the entered digits, after the 784 prefix) where the final 4-digit group starts — matches formatEmiratesId's 4-4-4 grouping. */
const LAST_GROUP_DIGIT_START = 8;

/**
 * Masks the final 4-digit group of a formatted Emirates ID, keeping dashes/spaces and the
 * first two groups intact. Fixed to that group's position (not "last 4 typed so far") so
 * digits already masked stay masked as the user keeps typing further into that group.
 * Uses '∗' (asterisk operator, U+2217) rather than a plain '*' — the plain asterisk glyph
 * sits inconsistently against the '-' separators in this font; the asterisk operator sits
 * centered on the line, matching the hyphen. (Unconfirmed visually — flag it if it still
 * looks off and note which way: too high/low/small.)
 */
const MASK_CHAR = '∗';

function maskEmiratesIdDisplay(formatted: string): string {
    const chars = formatted.split('');
    const digitIndices: number[] = [];
    chars.forEach((char, i) => {
        if (/\d/.test(char)) {
            digitIndices.push(i);
        }
    });
    digitIndices.slice(LAST_GROUP_DIGIT_START, LAST_GROUP_DIGIT_START + 4).forEach((idx) => {
        chars[idx] = MASK_CHAR;
    });
    return chars.join('');
}

/** Mobile-only display grouping: 784 - XXXX - XXXX - XXXX (even 4-digit blocks). */
function formatEmiratesId(text: string): string {
    let rawInputDigits = text.replace(/\D/g, '');
    let digits = '';

    if (rawInputDigits.startsWith(EMIRATES_ID_PREFIX)) {
        digits = rawInputDigits;
    } else {
        digits = EMIRATES_ID_PREFIX + rawInputDigits;
    }

    if (digits === EMIRATES_ID_PREFIX || digits === '') {
        return '';
    }

    if (digits.length > 15) {
        digits = digits.substring(0, 15);
    }

    let formatted = EMIRATES_ID_PREFIX;
    if (digits.length > 3) {
        formatted += ' - ' + digits.substring(3, 7);
    }
    if (digits.length > 7) {
        formatted += ' - ' + digits.substring(7, 11);
    }
    if (digits.length > 11) {
        formatted += ' - ' + digits.substring(11, 15);
    }
    return formatted;
}

function EmiratesIdStep({
    emiratesId,
    setEmiratesId,
    onContinue,
    isLoading,
    error,
}: Readonly<{
    emiratesId: string;
    setEmiratesId: (value: string) => void;
    onContinue: () => void;
    isLoading: boolean;
    error?: string | null;
}>) {
    const { t } = useTranslation();
    const colors = useUIColors();
    const { isRTL } = useI18nContext();

    const isValid = emiratesId.length === 24;
    const displayValue = emiratesId.startsWith(EMIRATES_ID_PREFIX_FORMATTED)
        ? emiratesId.substring(EMIRATES_ID_PREFIX_FORMATTED.length)
        : '';
    const maskedDisplayValue = maskEmiratesIdDisplay(displayValue);
    const enteredDigits = displayValue.replace(/\D/g, '');

    /**
     * The field is always masked, so onChangeText never sees the real digits under the
     * last-4 asterisks — we have to reconcile against maskedDisplayValue instead of the
     * raw digits. Rather than assuming the edit happened at the tail (which deletes/inserts
     * the wrong digit whenever the user moves the cursor and edits mid-string), diff the
     * previous and next masked strings by common prefix/suffix to find WHERE the edit
     * happened, then translate that span onto enteredDigits by counting digits in the
     * corresponding slice of the unmasked displayValue (same length/positions as the
     * masked string, so indices line up 1:1).
     */
    const handleChangeText = (text: string) => {
        const prev = maskedDisplayValue;
        const maxCommon = Math.min(prev.length, text.length);

        let prefixLen = 0;
        while (prefixLen < maxCommon && prev[prefixLen] === text[prefixLen]) {
            prefixLen++;
        }

        let suffixLen = 0;
        const maxSuffix = maxCommon - prefixLen;
        while (
            suffixLen < maxSuffix &&
            prev[prev.length - 1 - suffixLen] === text[text.length - 1 - suffixLen]
        ) {
            suffixLen++;
        }

        const digitsBeforeEdit = (displayValue.slice(0, prefixLen).match(/\d/g) ?? []).length;
        const digitsRemoved = (displayValue.slice(prefixLen, prev.length - suffixLen).match(/\d/g) ?? []).length;
        const digitsInserted = text.slice(prefixLen, text.length - suffixLen).replace(/\D/g, '');

        const nextDigits =
            enteredDigits.slice(0, digitsBeforeEdit) +
            digitsInserted +
            enteredDigits.slice(digitsBeforeEdit + digitsRemoved);

        setEmiratesId(nextDigits ? formatEmiratesId(EMIRATES_ID_PREFIX + nextDigits) : '');
    };

    return (
        <View style={styles.stepContainer}>
            <Logo size="large" />

            <Typography variant="title2" color="primary" style={styles.centeredTitle}>
                {t('auth.login')}
            </Typography>

            <View style={styles.inputGroup}>
                <TextInput
                    label={t('profile.emiratesId')}
                    placeholder={t('auth.emiratesIdInputPlaceholder')}
                    value={maskedDisplayValue}
                    onChangeText={handleChangeText}
                    errorTextColor={ERROR_TEXT_COLOR}
                    {...(error ? { error } : {})}
                    leftIcon={
                        <Typography style={[styles.prefixText, { color: colors.fontPrimary }]}>
                            {EMIRATES_ID_PREFIX_FORMATTED}
                        </Typography>
                    }
                    keyboardType="numeric"
                    // The atom defaults this to true. In Arabic the input is
                    // content-width, so the first digit re-lays it out and the
                    // re-focus selected that digit — the next keystroke then
                    // replaced it. Select-all is also risky on a masked ID
                    // (one key wipes it), so it's off in both languages.
                    selectTextOnFocus={false}
                    // Emirates ID is a number — keep "784 - xxxx - xxxx - xxxx"
                    // reading LTR in Arabic, but right-aligned in the field.
                    // Under RTL the TextInput atom renders leftIcon after the
                    // input, so lock this row to LTR; row-reverse then puts the
                    // prefix just before the digits. Label/error stay RTL.
                    {...(isRTL
                        ? {
                              inputContainerStyle: styles.emiratesIdRowLtr,
                              inputStyle: styles.emiratesIdInputLtr,
                          }
                        : {})}
                />
            </View>

            <Button
                title={isLoading ? t('common.loading') : t('common.continue')}
                variant="primary"
                onPress={onContinue}
                fullWidth
                disabled={!isValid || isLoading}
            />
        </View>
    );
}

function OtpStep({
    otp,
    setOtp,
    resendTimer,
    onVerify,
    onResend,
    onClose,
    type,
    contactInfo,
    isLoading,
    error,
}: Readonly<{
    otp: string;
    setOtp: (value: string) => void;
    resendTimer: number;
    onVerify: () => void;
    onResend: () => void;
    onClose: () => void;
    type: 'mobile' | 'email';
    contactInfo: string;
    isLoading: boolean;
    error?: string | null;
}>) {
    const { t } = useTranslation();
    const colors = useUIColors();
    const router = useRouter();

    // Contentful stores this flow's resend-OTP copy under the 'auth' namespace
    // (not 'otp' — that namespace is a separate/unrelated string set used by
    // OTPVerificationScreen and web's LoginDialog), so read from 'auth' here.
    const resendLabel =
        resendTimer > 0
            ? insertOtpResendCountdownInTemplate(t('auth.resendOTPTimer'), resendTimer)
            : t('auth.resendOTP');

    return (
        <View style={[styles.stepContainer, { alignItems: 'flex-start' }]}>
            <Logo size="large" style={styles.otpStepLogo} />

            <Typography variant="headline" color="primary" style={styles.verifyIdentityTitle}>
                {t('auth.verifyIdentity')}
            </Typography>

            <Typography variant="footnote" color="secondary" style={styles.verificationSentText}>
                {type === 'mobile' ? t('auth.mobileVerificationSent') : t('auth.emailVerificationSent')}{' '}
                {contactInfo ? (
                    <Typography variant="footnote" style={{ color: colors.fontHighContrast }}>
                        {forceLtr(contactInfo)}
                    </Typography>
                ) : null}
            </Typography>

            <View style={styles.otpInputWrap}>
                <OTPInput
                    length={4}
                    value={otp}
                    onChangeText={setOtp}
                    loading={isLoading}
                    boxSize={52}
                    gap={16}
                    boxRadius={8}
                    fontSize={18}
                    errorTextColor={ERROR_TEXT_COLOR}
                    {...(error ? { errorMessage: error } : {})}
                />
            </View>

            <View style={styles.otpFootLinks}>
                <Typography variant="footnote" color="secondary" style={styles.centeredText}>
                    {t('auth.didntReceiveCode')}{' '}
                    <Typography
                        variant="footnote"
                        style={{ color: colors.fontPrimary }}
                        onPress={resendTimer <= 0 && !isLoading ? onResend : undefined}
                    >
                        {resendLabel}
                    </Typography>
                </Typography>

                <Pressable
                    onPress={() => {
                        trackEmiratesIdRaiseSupportClick(
                            type === 'email' ? 'EMAIL_OTP' : 'MOBILE_OTP',
                        );
                        onClose();
                        router.push({ pathname: '/service-request-create', params: { authFlow: 'true' } } as never);
                    }}
                >
                    <Typography variant="footnote" color="secondary" style={styles.centeredText}>
                        {t('auth.noAccessToPhone')}{' '}
                        <Typography variant="footnote" style={[styles.raiseSupportTicketText, { color: colors.fontErrorText }]}>
                            {t('auth.raiseSupportTicket')}
                        </Typography>
                    </Typography>
                </Pressable>
            </View>

            <Button
                title={isLoading ? t('common.loading') : t('auth.verifyAndContinue')}
                variant="primary"
                onPress={onVerify}
                fullWidth
                disabled={otp.length !== 4 || isLoading}
            />
        </View>
    );
}

export function EmiratesIdLoginSheet({ visible, onClose }: Readonly<EmiratesIdLoginSheetProps>) {
    const colors = useUIColors();
    const { t, getTranslations } = useTranslation();
    const otpResendSeconds = useEmiratesIdOtpResendSeconds();
    const [emiratesId, setEmiratesId] = useState('');
    const [otp, setOtp] = useState('');
    // App loader is shown only while Resend OTP is in flight.
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    // The countdown is kept as an end time, not a number we decrement: JS timers
    // pause while the app is in the background, so a decrementing counter would
    // freeze there. Seconds left are always worked out from the clock instead.
    const [resendEndsAt, setResendEndsAt] = useState(() => Date.now() + otpResendSeconds * 1000);
    const [now, setNow] = useState(() => Date.now());
    const resendTimer = Math.max(0, Math.ceil((resendEndsAt - now) / 1000));
    const startResendTimer = useCallback((seconds: number) => {
        const startedAt = Date.now();
        setNow(startedAt);
        setResendEndsAt(startedAt + seconds * 1000);
    }, []);
    const {
        step,
        isLoading,
        error,
        successMessage,
        tempEmail,
        tempPhoneNumber,
        submitEmiratesId,
        verifyMobileOtp,
        verifyEmailOtp,
        resendMobileOtp,
        resendEmailOtp,
        reset,
        clearError,
        clearSuccessMessage,
    } = useEmiratesIdLogin();

    // Starts the flow fresh when the sheet opens, and again if Contentful sends a
    // different OTP resend time while it is open (e.g. app back in the
    // foreground). Nothing else resets it: `getTranslations` changes identity on
    // every Contentful refresh even when nothing changed, and that alone must
    // not wipe a flow in progress.
    const wasVisibleRef = useRef(false);
    const appliedResendSecondsRef = useRef(otpResendSeconds);
    useEffect(() => {
        const justOpened = visible && !wasVisibleRef.current;
        const resendSecondsChanged =
            visible && otpResendSeconds !== appliedResendSecondsRef.current;
        wasVisibleRef.current = visible;
        appliedResendSecondsRef.current = otpResendSeconds;
        if (justOpened || resendSecondsChanged) {
            if (justOpened) {
                trackEmiratesIdLoginStartClick();
            }
            setEmiratesId('');
            setOtp('');
            startResendTimer(otpResendSeconds);
            reset();
            // Emirates ID sheet copy flows through the Contentful-aware
            // translation pipeline (translationService.ts merges CMS
            // namespaces over bundled i18n). Dump every namespace this flow
            // reads from — not just a handful of keys — so device logs
            // (EXPO_PUBLIC_ENABLE_LOGS) show all the data actually in play,
            // whether it came from Contentful or the bundled fallback.
            const allTranslations = getTranslations();
            logger.debug('EmiratesIdLoginSheet', 'Resolved translation namespaces on open', {
                auth: allTranslations.auth,
                otp: allTranslations.otp,
                profile: allTranslations.profile,
                common: allTranslations.common,
            });
        }
    }, [visible, otpResendSeconds, reset, getTranslations, startResendTimer]);

    const isCountingDown = (step === 'MOBILE_OTP' || step === 'EMAIL_OTP') && resendTimer > 0;
    useEffect(() => {
        if (!isCountingDown) {
            return undefined;
        }
        const interval = setInterval(() => setNow(Date.now()), 1000);
        // Coming back from the background: show the real time left right away
        // instead of waiting for the next tick.
        const subscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
                setNow(Date.now());
            }
        });
        return () => {
            clearInterval(interval);
            subscription.remove();
        };
    }, [isCountingDown]);

    const handleSetEmiratesId = (value: string) => {
        setEmiratesId(value);
        if (error) {
            clearError();
        }
    };

    const handleSetOtp = (value: string) => {
        setOtp(value);
        if (error) {
            clearError();
        }
    };

    const handleContinue = async () => {
        const didAdvance = await submitEmiratesId(emiratesId);
        if (didAdvance) {
            setOtp('');
            startResendTimer(otpResendSeconds);
        }
    };

    const handleVerify = async () => {
        if (step === 'MOBILE_OTP') {
            // Emirates ID login now completes directly after mobile OTP
            // verification (email OTP step commented out in
            // useEmiratesIdLogin.ts, matching web's LoginDialog.web.tsx
            // bypass) — success here means login is done, not "advance to
            // the next step".
            const didSucceed = await verifyMobileOtp(otp);
            if (didSucceed) {
                onClose();
            }
            return;
        }

        const didSucceed = await verifyEmailOtp(otp);
        if (didSucceed) {
            onClose();
        }
    };

    const handleResend = async () => {
        setIsResendingOtp(true);
        try {
            const didResend = step === 'EMAIL_OTP' ? await resendEmailOtp() : await resendMobileOtp();
            if (didResend) {
                setOtp('');
                startResendTimer(otpResendSeconds);
            }
        } finally {
            setIsResendingOtp(false);
        }
    };

    return (
        <>
            <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
                <View style={styles.overlay}>
                    <View style={[styles.card, { backgroundColor: colors.white }]}>
                        {/* Only way to dismiss — tapping outside no longer closes the sheet. */}
                        <Pressable
                            style={styles.closeButton}
                            onPress={onClose}
                            disabled={isLoading}
                            hitSlop={8}
                            accessibilityRole="button"
                            accessibilityLabel={t('common.close')}
                        >
                            <XIcon size={20} color={colors.fontPrimary} />
                        </Pressable>

                        {step === 'EMIRATES_ID' ? (
                            <EmiratesIdStep
                                emiratesId={emiratesId}
                                setEmiratesId={handleSetEmiratesId}
                                onContinue={handleContinue}
                                isLoading={isLoading}
                                error={error}
                            />
                        ) : (
                            <OtpStep
                                otp={otp}
                                setOtp={handleSetOtp}
                                resendTimer={resendTimer}
                                onVerify={handleVerify}
                                onResend={handleResend}
                                onClose={onClose}
                                type={step === 'EMAIL_OTP' ? 'email' : 'mobile'}
                                contactInfo={
                                    step === 'EMAIL_OTP'
                                        ? maskEmailForOtp(tempEmail)
                                        : tempPhoneNumber
                                }
                                isLoading={isLoading}
                                error={error}
                            />
                        )}
                    </View>
                    {/* App loader while Resend OTP is in flight. Rendered inside the
                        sheet's Modal so it sits on top of it: outside, Android drew it
                        behind the Modal (plain overlay View) and it never showed. */}
                    <FullScreenLoader visible={isResendingOtp} />
                </View>
            </Modal>
            <Toast
                visible={Boolean(successMessage)}
                message={successMessage ?? ''}
                onHide={clearSuccessMessage}
            />
        </>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    card: {
        width: '100%',
        maxWidth: 360,
        borderRadius: 16,
        padding: 30,
        position: 'relative',
    },
    closeButton: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 1,
    },
    stepContainer: {
        gap: 20,
        alignItems: 'center',
    },
    otpStepLogo: {
        alignSelf: 'center',
    },
    centeredTitle: {
        textAlign: 'center',
    },
    centeredText: {
        textAlign: 'center',
    },
    inputGroup: {
        gap: 8,
        width: '100%',
    },
    prefixText: {
        fontSize: 16,
    },
    // row-reverse packs from the right edge (flex-start), so the
    // "784 - " + digits group sits right-aligned while reading LTR.
    emiratesIdRowLtr: {
        direction: 'ltr',
        flexDirection: 'row-reverse',
        justifyContent: 'flex-start',
    },
    // Content-width instead of the atom's flex: 1, so the prefix stays
    // right next to the digits rather than at the far left edge.
    emiratesIdInputLtr: {
        flex: 0,
        flexShrink: 1,
        textAlign: 'left',
        writingDirection: 'ltr',
    },
    verifyIdentityTitle: {
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        fontSize: 18,
        lineHeight: 18,
        letterSpacing: 0,
    },
    raiseSupportTicketText: {
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        fontSize: 13,
        lineHeight: 13,
        letterSpacing: 0,
    },
    verificationSentText: {
        marginTop: -8,
    },
    otpInputWrap: {
        alignItems: 'center',
        width: '100%',
        marginTop: 8,
    },
    otpFootLinks: {
        gap: 12,
        width: '100%',
        alignItems: 'center',
    },
});

export default EmiratesIdLoginSheet;
